/**
 * Single source of truth for sidebar navigation.
 *
 * Consumed by:
 * - layouts/ModernMainLayout.tsx (sidebar rendering + header breadcrumb titles)
 * - components/SearchCommand.tsx (Cmd+K command palette static entries)
 *
 * Visibility gating is data-driven via the per-item `gate` field, applied by
 * `filterNavigation`. Route-level guards live separately in App.tsx
 * (InternalAdsToolsRoute, InternalOnlyRoute).
 */
import {
	Activity,
	BarChart3,
	BookOpen,
	Bot,
	ChartLine,
	ClipboardCheck,
	ClipboardList,
	Coins,
	DollarSign,
	Eye,
	FileChartColumn,
	FileCheck,
	FileSignature,
	FileText,
	Filter,
	Gauge,
	Hammer,
	Images,
	LayoutDashboard,
	LayoutGrid,
	LayoutTemplate,
	LifeBuoy,
	Megaphone,
	PhoneCall,
	ScanSearch,
	Search,
	Settings,
	ShieldCheck,
	SlidersHorizontal,
	Swords,
	Users,
	Wrench,
	Zap,
} from "lucide-react";
import type React from "react";
import { canAccessInternalAdsTools } from "@/lib/adsToolsAccess";
import { isElevatedRole } from "@/lib/roles";
import type { UserProfile, Workspace } from "@/types/auth";
import { COPY } from "@/lib/copyTokens";
import { FEATURE_FLAGS } from "@/config/environment";
import { enabledFeatures, type AdsFeature } from "@/lib/entitlements";

/**
 * Per-item visibility gates:
 * - "elevated":           isElevatedRole(platform_role) (lib/roles.ts)
 * - "agileco":            elevated OR @agileandco.com email OR VITE_DISABLE_AUTH
 *                         (identical to the canAccessInternalAdsTools predicate)
 * - "elevatedAndAgileco": elevated AND @agileandco.com email
 * - "adsTools":           canAccessInternalAdsTools
 */
export type NavigationGate =
	| "elevated"
	| "agileco"
	| "elevatedAndAgileco"
	| "adsTools"
	// Inverse of adsTools: shows ONLY to non-operators (real clients). Used to
	// keep "Ads Performance" top-level for clients while operators see it grouped
	// inside "Ads Tools" instead of duplicated at the top level.
	| "clientAds";
export type NavigationBadgeKey =
	| "humanQaPending"
	| "managementEasyYes"
	| "searchTermsEasy"
	| "gbpPending"
	| "imageAssetsMissing"
	| "whatConvertsPending"
	| "accountAuditFailed"
	| "weeklyManagementPending"
	| "plannerApprovalsPending"
	| "supportTicketsOpen";

// Kept for backwards compatibility with any callers still checking the old set;
// prefer NAV_BADGE_METADATA below. Every badge now renders `{count} {label}`
// with a full-sentence tooltip on hover, so a bare-number vs "N pending"
// switch is no longer used.
export const NUMERIC_NAV_BADGE_KEYS: ReadonlySet<NavigationBadgeKey> = new Set([
	"managementEasyYes",
	"searchTermsEasy",
]);

/**
 * What each numeric sidebar badge means.
 *
 * Contract: every numeric badge is a count of items on the linked page that
 * need the operator's input or action. `label` is the compact chip text
 * ({count} rendered before it, e.g. "4 to review") and MUST be an
 * action-focused verb ("to review", "to approve"), never an item type
 * ("easy yes", "in QA"). `tooltip` is the full sentence shown on hover so
 * the reader can confirm what action the badge is asking for.
 * `describe(count)` is the same sentence with the count interpolated for
 * clarity ("4 items waiting for your ...").
 */
export const NAV_BADGE_METADATA: Record<
	NavigationBadgeKey,
	{ label: string; tooltip: string; describe: (count: number) => string }
> = {
	humanQaPending: {
		label: "to QA",
		tooltip: "Articles waiting for your QA review.",
		describe: (count) =>
			`${count} article${count === 1 ? "" : "s"} waiting for your QA review.`,
	},
	managementEasyYes: {
		label: "to approve",
		tooltip:
			"Weekly Management actions waiting for your Yes/No decision.",
		describe: (count) =>
			`${count} Weekly Management action${count === 1 ? "" : "s"} waiting for your Yes/No decision.`,
	},
	searchTermsEasy: {
		label: "to review",
		tooltip:
			"Negative-keyword suggestions waiting for your Yes/No decision.",
		describe: (count) =>
			`${count} negative-keyword suggestion${count === 1 ? "" : "s"} waiting for your Yes/No decision.`,
	},
	gbpPending: {
		label: "to fix",
		tooltip: "Google Business Profile recommendations waiting to be applied.",
		describe: (count) =>
			`${count} Google Business Profile recommendation${count === 1 ? "" : "s"} waiting to be applied.`,
	},
	imageAssetsMissing: {
		label: "missing",
		tooltip:
			"Performance Max image slots that still need assets before launch.",
		describe: (count) =>
			`${count} Performance Max image slot${count === 1 ? "" : "s"} still missing assets. Launching PMax without them will be rejected by Google.`,
	},
	whatConvertsPending: {
		label: "to score",
		tooltip: "WhatConverts leads waiting for a quotable / not-quotable score.",
		describe: (count) =>
			`${count} WhatConverts lead${count === 1 ? "" : "s"} waiting for a quotable / not-quotable score.`,
	},
	accountAuditFailed: {
		label: "failed",
		tooltip:
			"Account Audit checks currently failing — the latest audit surfaced these issues.",
		describe: (count) =>
			`${count} Account Audit check${count === 1 ? "" : "s"} currently failing on the latest audit run.`,
	},
	weeklyManagementPending: {
		label: "to approve",
		tooltip:
			"Weekly Management proposals from the scheduled cron waiting for your approval.",
		describe: (count) =>
			`${count} Weekly Management proposal${count === 1 ? "" : "s"} waiting for your approval.`,
	},
	plannerApprovalsPending: {
		label: "to approve",
		tooltip:
			"Monthly plan submissions across all clients waiting for your approval.",
		describe: (count) =>
			`${count} monthly plan submission${count === 1 ? "" : "s"} waiting for your approval.`,
	},
	supportTicketsOpen: {
		label: "open",
		tooltip: "Open support tickets assigned to you (not yet done).",
		describe: (count) =>
			`${count} open support ticket${count === 1 ? "" : "s"} assigned to you.`,
	},
};

export type NavigationFlags = {
	/** isElevatedRole(userProfile.platform_role) */
	isElevated: boolean;
	/** Raw @agileandco.com email check (no role/env fallback) */
	isAgileCo: boolean;
	/** canAccessInternalAdsTools(userProfile) */
	canAdsTools: boolean;
	/**
	 * The current workspace's entitled ads features (F1..F9). Ships DARK: with
	 * ADS_ENTITLEMENTS_ENABLED off the backend sends every key, so a
	 * `featureGate` never hides anything until the flag is turned on. An
	 * SEO-only tenant (with the flag on) gets an empty set, hiding ads items.
	 */
	enabledAdsFeatures: Set<AdsFeature>;
};

export type NavigationChildLinkItem = {
	type?: "link";
	title: string;
	url: string;
	icon?: React.ElementType;
	color?: string;
	gate?: NavigationGate;
	/** Entitlement gate: hide unless the workspace has this ads feature. */
	featureGate?: AdsFeature;
	badgeKey?: NavigationBadgeKey;
	/**
	 * When true the item is shown but not navigable — rendered greyed with a
	 * lock icon. Used for features that aren't ready yet (e.g. Content
	 * Preferences before it's wired into the backend pipeline).
	 */
	locked?: boolean;
	/**
	 * Pathname prefixes that also count as active for this link (in addition
	 * to its exact url match). A prefix ending in "/" uses plain startsWith;
	 * otherwise pathname === prefix || pathname.startsWith(prefix + "/").
	 */
	match?: string[];
};

export type NavigationChildGroupItem = {
	type: "group";
	title: string;
	items: NavigationChildLinkItem[];
	gate?: NavigationGate;
	featureGate?: AdsFeature;
};

export type NavigationChildItem = NavigationChildLinkItem | NavigationChildGroupItem;

export type NavigationItem =
	| {
			title: string;
			url: string;
			icon: React.ElementType;
			gate?: NavigationGate;
			featureGate?: AdsFeature;
			match?: string[];
			/** Sidebar count badge — only for the no-children (leaf) variant. */
			badgeKey?: NavigationBadgeKey;
			items?: never;
	  }
	| {
			title: string;
			icon: React.ElementType;
			items: NavigationChildItem[];
			gate?: NavigationGate;
			featureGate?: AdsFeature;
			/** Give the flat child list the sidebar's full right-hand width
			 * (drops the submenu's right margin and most of its right padding).
			 * Opt-in per group so the SEO groups keep their spacing. */
			wideChildren?: boolean;
			url?: never;
			match?: never;
			badgeKey?: never;
	  };

export function isNavigationChildLink(
	item: NavigationChildItem,
): item is NavigationChildLinkItem {
	return "url" in item;
}

export function isNavigationChildGroup(
	item: NavigationChildItem,
): item is NavigationChildGroupItem {
	return item.type === "group";
}

export function buildNavigationFlags(
	userProfile: Pick<UserProfile, "email" | "platform_role"> | null | undefined,
	workspace?: Workspace | null,
): NavigationFlags {
	return {
		isElevated: isElevatedRole(userProfile?.platform_role),
		isAgileCo: userProfile?.email?.toLowerCase().endsWith("@agileandco.com") ?? false,
		canAdsTools: canAccessInternalAdsTools(userProfile),
		// enabledFeatures returns ALL features when the workspace is null or the
		// backend hasn't sent enabled_features (ship dark) — so a featureGate is
		// inert until the flag is on and a package/tier actually restricts it.
		enabledAdsFeatures: enabledFeatures(workspace),
	};
}

function featureAllows(
	featureGate: AdsFeature | undefined,
	flags: NavigationFlags,
): boolean {
	return !featureGate || flags.enabledAdsFeatures.has(featureGate);
}

function gateAllows(gate: NavigationGate | undefined, flags: NavigationFlags): boolean {
	if (!gate) return true;
	switch (gate) {
		case "elevated":
			return flags.isElevated;
		case "agileco":
			// Same predicate as canAccessInternalAdsTools today (elevated OR
			// @agileandco.com OR VITE_DISABLE_AUTH) — shared via canAdsTools.
			return flags.canAdsTools;
		case "elevatedAndAgileco":
			return flags.isElevated && flags.isAgileCo;
		case "adsTools":
			return flags.canAdsTools;
		case "clientAds":
			// Only real clients (not internal ads operators) — operators get the
			// grouped copy under "Ads Tools" instead.
			return !flags.canAdsTools;
	}
}

/** Applies per-item gates recursively and drops groups/parents left empty. */
export function filterNavigation(
	items: NavigationItem[],
	flags: NavigationFlags,
): NavigationItem[] {
	return items.flatMap((item): NavigationItem[] => {
		if (!gateAllows(item.gate, flags) || !featureAllows(item.featureGate, flags))
			return [];
		if (!item.items) return [item];

		const children = item.items.flatMap((child): NavigationChildItem[] => {
			if (!gateAllows(child.gate, flags) || !featureAllows(child.featureGate, flags))
				return [];
			if (isNavigationChildGroup(child)) {
				const links = child.items.filter(
					(link) => gateAllows(link.gate, flags) && featureAllows(link.featureGate, flags),
				);
				return links.length ? [{ ...child, items: links }] : [];
			}
			return [child];
		});

		return children.length ? [{ ...item, items: children }] : [];
	});
}

// Client nav-redesign labels (2026-08 meeting, "Client-Facing Navigation Menu
// Redesign" doc). Reused for the sidebar labels AND the header breadcrumb titles below.
const seoContentLabel = "SEO Content";
const googleAdsLabel = "Google Ads";

export const navigationItems: NavigationItem[] = [
	// Combined client dashboard: the single canonical home for SEO, ads, leads,
	// local, reviews, site health, content, and completed work.
	{
		title: "Dashboard",
		url: "/dashboard",
		icon: LayoutDashboard,
	},
	{
		// The layout rewrites this href via buildCentralChatHref at render time.
		title: "Emjae",
		url: "/chat",
		icon: Bot,
	},
	{
		// Client-facing usage in credits — ungated on purpose (visible to
		// clients). Distinct from the operator "Usage & Cost" page under the
		// admin group, which shows raw provider dollar cost. Kept top-level
		// (not folded into the new Settings shell) — it isn't one of the six
		// consolidated Settings sections and staging's attempt to nest it
		// under the old Settings nav tree doesn't apply to the new structure.
		title: "Usage & credits",
		url: "/credits",
		icon: Coins,
	},
	// Audit → Proposal is placed under Ads Tools below so related sales
	// workflows stay together.
	{
		title: seoContentLabel,
		icon: FileText,
		// Nav redesign: the "Plan & Research" sub-header is removed (it was never a
		// clickable page) and Keyword Research is ordered before Content Planner to
		// follow the research → plan workflow.
		items: [
			{ title: "Keyword Research", url: "/seo/keyword-research" },
			{
				title: "Content Planner",
				url: "/content/planner",
				match: ["/content/planner"],
			},
			// Human QA queue. The badge count is already computed in
			// ModernMainLayout (blogCreationService.listHumanQaQueue) — this is
			// the nav entry that consumes it. Elevated-only: non-elevated users
			// don't have QA authority so a badge here would just be noise.
			{
				title: "QA Review",
				url: "/content/human-qa",
				gate: "elevated",
				badgeKey: "humanQaPending",
			},
			{ title: "Competitor Compare", url: "/seo/competitor-compare" },
			{ title: "Blog Templates", url: "/content/templates" },
		],
	},
	{
		// Its own surface, not a content sub-item. These build pages for paid
		// traffic and for local intent, from a client's own records — a
		// different job from the blog pipeline, and a different audience:
		// account managers and whoever owns a client's WordPress, not just
		// content staff.
		title: "Page Builder",
		icon: LayoutTemplate,
		items: [
			// Hidden rather than `locked`. `locked` reads as "coming soon",
			// which is true of Location and Service Pages below and false
			// here: the tool exists and works, it just cannot run in an
			// environment with no seeded templates. Showing it locked would
			// promise something this environment will not deliver.
			...(FEATURE_FLAGS.landingPagesEnabled
				? [
						{ title: "Overview", url: "/content/page-builder/overview" },
						{ title: "Landing Pages", url: "/content/landing-pages" },
					]
				: []),
			// Shown deliberately rather than hidden. Both are real, adjacent
			// jobs the same machinery already half-does — service_page_crew
			// exists, and location pages are the obvious multi-location case.
			// Naming them here says what this surface is FOR, so nobody
			// mistakes it for a one-page tool.
			{ title: "Location Pages", url: "/content/location-pages", locked: true },
			{ title: "Service Pages", url: "/content/service-pages", locked: true },
		],
	},
	{
		title: "Site Health",
		icon: Activity,
		items: [
			{ title: "Technical Audit", url: "/audit/technical", match: ["/audit/technical"] },
			// Local Falcon-backed GBP recommendations. Sits beside Technical
			// Audit rather than inside it: GBP findings are per-location, with no
			// crawl run, no per-URL severity and no apply/revert lifecycle.
			{
				title: "Google Business Profile",
				url: "/seo/gbp",
				match: ["/seo/gbp"],
				badgeKey: "gbpPending",
			},
			// Nav redesign: Tickets moved out of Site Health into the new top-level
			// "Get Support" category below.
			// Gate matches the page's own isElevated block (GH #949) so
			// non-elevated users don't see a link into an access-denied page.
			{ title: "Internal Pages", url: "/seo/internal-pages", gate: "elevated" },
			// Hidden — route stays live in App.tsx, kept out of the sidebar:
			// { title: "Comprehensive Audit", url: "/comprehensive-site-audit" },
			// Hidden — not production-ready; routes stay live in App.tsx:
			// { title: "Schema Management", url: "/seo/schema-management" },
			// { title: "SERP Preview", url: "/seo/serp-preview" },
			// { title: "Core Web Vitals", url: "/seo/core-web-vitals" },
		],
	},
	{
		// "Google Ads" per the redesign (room for Meta Ads later). Internal
		// operator group — gate unchanged.
		title: googleAdsLabel,
		icon: Megaphone,
		gate: "adsTools",
		// Measured 2026-09-24 in Chromium, Inter 14px: a leaf label here got
		// 118px, and Keyword Research (122), Campaign Settings (125) and
		// Competitive Insights (134) clipped. The submenu spent 14px on a
		// right margin and 10px on right padding, i.e. dead space. Taking it
		// back gives 138px, enough for all three at the spec's own wording,
		// without widening the sidebar or moving the left indent.
		wideChildren: true,
		// ⛔ ONE LEVEL, IN THE SPEC'S ORDER.
		// Art's 2026-09-22 review counted 14 leaf items against the doc's 11,
		// plus an "Ads Management" layer the doc does not have, and asked for
		// Management above the building tools. The two wrapper groups
		// ("Campaign Builder", "Ads Management") are gone: with 11 day-to-day
		// pages, a second level buys nothing and cost an extra click on every
		// one of them.
		//
		// Flattening also buys width back, which is the honest half of the
		// truncation fix. The sidebar is a fixed 14rem and truncates any label
		// that outgrows it, and a nested item starts further in — "Client
		// Dashboard" (16 chars) never clipped while "Keyword Research" (also
		// 16) did, purely because the second sat a level deeper. Every label
		// here now starts at the shallower indent, so the five Art reported
		// clipped may already fit.
		//
		// ⚠ Names are the spec's own words, NOT shortened to guess at width.
		// An earlier pass here renamed Keyword Research to "Keywords" and
		// Campaign Settings to "Settings"; both diverge from Chans's spec §1,
		// and campaignSettingsNavGuards.test.ts exists precisely to hold that
		// vocabulary. PMax and WhatConverts are shorter because THE SPEC calls
		// them that, not to save pixels. If anything still clips, widening the
		// sidebar is off the table (it is shared with the SEO nav) so the
		// wording is Art's call as the person reading it daily.
		//
		// CORE-M-11.b: Current Campaigns is not here; it folded into the
		// Management drill-down and its route redirects.
		//
		// Order below is Chans's spec §1. The last three (Campaign QA, Search
		// Terms, Account Audit) are NOT in the spec's 11: they are pages on
		// their way out, so they sit after the eleven rather than among them.
		// Search Terms stays until the M-03 negatives port lands; Account
		// Audit until Core audits an account on its own (Chans's Q-1).
		items: [
			{
				// CORE-D-01: first item, an all-client overview. Chans's spec
				// §10: "it doesn't belong buried under management."
				title: "Client Dashboard",
				url: "/ads-tools/client-dashboard?platform=google-ads",
				icon: LayoutGrid,
			},
			{
				// Above the building tools, per Art: this is where an operator's
				// day starts.
				title: "Management",
				url: "/ads-tools/management?platform=google-ads",
				icon: SlidersHorizontal,
				badgeKey: "managementEasyYes",
			},
			{
				title: "Campaign Builder",
				url: "/ads-tools/campaigns?platform=google-ads&campaign_tab=create",
				icon: Hammer,
			},
			{
				title: "Keyword Research",
				url: "/ads-tools/keyword-research?platform=google-ads",
				icon: Search,
			},
			{
				title: "Campaign Settings",
				url: "/ads-tools/campaign-settings?platform=google-ads",
				icon: Wrench,
			},
			{
				// CORE-P-01: Performance Max is "its own animal" per spec §9.
				// The spec's own nav calls it PMax, which also stops it
				// clipping and stops it reading as a sibling of Performance.
				title: "PMax",
				url: "/ads-tools/pmax?platform=google-ads",
				icon: Zap,
			},
			{
				title: "Competitive Insights",
				url: "/ads-tools/competitive?platform=google-ads",
				icon: Swords,
			},
			{
				// ⚠ Shares a name with the top-level "Performance" (/overview),
				// which Art flagged. Left as the spec names it: renaming it
				// here would disagree with the spec, and the group heading
				// above already reads "Google Ads". Raised with Art rather
				// than decided here.
				title: "Performance",
				url: "/ads-tools/performance?platform=google-ads",
				icon: ChartLine,
			},
			{
				title: "Budget Pacing",
				url: "/ads-tools/budgets?platform=google-ads",
				icon: Gauge,
			},
			{
				// The spec calls this "WhatConverts"; the page itself says Leads.
				title: "WhatConverts",
				url: "/ads-tools/whatconverts",
				// main: action-count badge (one poll per surface), kept through CORE-G-4's flatten.
				badgeKey: "whatConvertsPending",
				icon: PhoneCall,
			},
			{
				title: "Image Assets",
				url: "/ads-tools/image-assets",
				// main: action-count badge (one poll per surface), kept through CORE-G-4's flatten.
				badgeKey: "imageAssetsMissing",
				icon: Images,
			},
			// ---- On their way out. Not among the spec's 11. ----
			{
				title: "Campaign QA",
				url: "/ads-tools/campaign-qa?platform=google-ads",
				icon: ClipboardCheck,
			},
			{
				// Stays until the M-03 negatives port lands on the Management tab.
				// Label is Art's pick (2026-09-24): the full "Search Terms &
				// Negatives" does not fit beside its count badge in the 14rem
				// sidebar. The page itself keeps its full name.
				title: "Search Terms",
				url: "/ads-tools/search-terms?platform=google-ads",
				icon: Filter,
				badgeKey: "searchTermsEasy",
			},
			{
				// Chans's Q-1: this can be hidden once Core audits the whole
				// account on its own and raises the issues. That background
				// audit does not exist yet, so hiding it now would remove a
				// capability with nothing behind it.
				title: "Account Audit",
				url: "/ads-tools/account-audit?platform=google-ads",
				// main: action-count badge (one poll per surface), kept through CORE-G-4's flatten.
				badgeKey: "accountAuditFailed",
				icon: ScanSearch,
			},
			// Read-only launch-layer reports — ships dark behind VITE_ADS_REPORTS_PANEL
			...(import.meta.env.VITE_ADS_REPORTS_PANEL === "true"
				? [
						{
							title: "Reports (Read-Only)",
							url: "/ads-tools/reports?platform=google-ads",
							icon: FileChartColumn,
						},
					]
				: []),
			// Audit → Proposal — a sales proposal for ads, so it lives here.
			// Flag-gated (proposalToolEnabled) + AgileCo-only, so it keeps its
			// own group: the gate hangs on the group, not the leaf.
			...(FEATURE_FLAGS.proposalToolEnabled
				? [
						{
							type: "group" as const,
							title: "Proposals",
							items: [
								{
									title: "Audit → Proposal",
									url: "/proposals",
									icon: FileSignature,
									gate: "agileco" as const,
								},
							],
						},
					]
				: []),
		],
	},
	// Planner (internal, staff-only): the Agile Planner rebuilt natively in Core
	// (Row 15 / #1221). Behind the agileco staff gate — hidden from clients entirely.
	// Clients (C2 consolidation): who clients ARE — one roster joining
	// contract ⋈ tenant ⋈ workspace, the ads lens, groups, and summaries.
	{
		title: "Clients",
		icon: Users,
		gate: "agileco" as const,
		items: [
			{ title: "Workspaces", url: "/clients" },
			{ title: "Ads Overview", url: "/clients/ads" },
			{ title: "Groups", url: "/clients/groups" },
			{ title: "Client Summary", url: "/clients/summary" },
		],
	},
	// Planner: work surfaces only — things staff DO (the client rosters
	// moved to the Clients section above).
	{
		title: "Planner",
		icon: ClipboardList,
		gate: "agileco" as const,
		items: [
			{ title: "Agile Plan", url: "/planner" },
			{ title: "Offsite Links", url: "/planner/offsite" },
			{
				title: "Approvals",
				url: "/planner/approvals",
				badgeKey: "plannerApprovalsPending",
			},
			{ title: "Task Catalog", url: "/planner/catalog" },
			{ title: "Feedback", url: "/planner/feedback" },
		],
	},
	// Get Support: one destination, no sub-items. The board IS the request
	// form -- "Add Task" opens the same modal the standalone /support page
	// used to be -- so a separate "New Request" child was two links to one
	// thing. /support now redirects here for anyone holding an old link.
	//
	// The "Tickets" child also used to fall back to /audit/tickets when the
	// ticketsBoard flag was off: a link labelled "Tickets" that showed
	// Technical Audit's manual-fix tickets, a different table. That is how
	// support tickets looked like they had disappeared.
	{
		title: "Get Support",
		icon: LifeBuoy,
		url: "/support/tickets",
		badgeKey: "supportTicketsOpen",
	},
	{
		title: "Settings",
		icon: Settings,
		items: [
			// The settings shell owns every client-scoped settings surface; the
			// sidebar stays a thin entry point (S4). staging's older nested
			// "Workspace" / "Brand & Content" groups (Workspace Settings,
			// Team & Account, Content Preferences, Image Assets) all redirect
			// into this same shell now (see SETTINGS_REDIRECT_ROUTES) so they
			// aren't carried over as separate nav entries. "Usage & credits"
			// isn't one of the six consolidated Settings sections — it's kept
			// as its own top-level nav item above (next to Emjae), not folded
			// in here.
			{ title: "Settings", url: "/settings", icon: Settings, color: "text-teal-dark" },
			{ title: "Brand Bible", url: "/brand-bible", icon: BookOpen, color: "text-teal-dark" },
			// Staff-only device toggles for in-progress features (nav redesign,
			// tickets, planner). Not behind those flags so it's always reachable.
			{ title: "Preview Features", url: "/internal/preview", icon: FileCheck, gate: "agileco", color: "text-coral" },
			{
				// Admin tooling lives under Settings (same config/ops family).
				// Elevated-only: a non-elevated user just sees the links above and
				// no Admin sub-group at all (filterNavigation drops the gated group).
				type: "group",
				title: "Platform Admin",
				gate: "elevated",
				items: [
					// The one entry point to the Admin Panel (O47); /platform/* redirects here.
					{ title: COPY.adminPanel.label, url: COPY.adminPanel.path, icon: ShieldCheck, color: "text-teal-dark" },
					{
						title: "Research Coverage",
						url: "/admin/research-coverage",
						icon: Eye,
						color: "text-teal-dark",
					},
					{
						title: "LLM Benchmarks",
						url: "/admin/llm-benchmarks",
						icon: BarChart3,
						color: "text-teal-dark",
					},
					{
						title: "Content Benchmarks",
						url: "/content/benchmarks",
						icon: BarChart3,
						color: "text-coral",
						gate: "elevatedAndAgileco",
					},
					{
						// Canonical URL; /cost-attribution stays routed as an alias.
						title: "Usage & Cost",
						url: "/usage",
						icon: DollarSign,
						color: "text-teal-dark",
						gate: "elevatedAndAgileco",
					},
				],
			},
		],
	},
];

// Route → header breadcrumb title mapping (exact pathname keys).
export const routeTitles: Record<string, string> = {
	"/dashboard": "Dashboard",
	// Legacy performance homes redirect to /dashboard.
	// "/chat" renamed by the Core rebrand (#1079).
	"/overview": "Performance",
	"/performance": "Performance",
	"/chat": "Emjae",
	"/proposals": "Audit → Proposal",
	"/settings": "Settings",
	"/settings/integrations": "Integrations",
	"/settings/content": "Content generation",
	"/settings/website": "Website",
	"/settings/media": "Media",
	"/settings/team": "Team & Account",
	[COPY.adminPanel.path]: COPY.adminPanel.label,
	"/brand-bible": "Brand Bible",
	"/workspace-settings": "Settings",
	"/account": "Team & Account",
	"/content/templates": "Blog Templates",
	"/content/page-builder/overview": "Page Builder Overview",
	"/content/landing-pages": "Landing Pages",
	"/content/planner": "Content Planner",
	"/content/benchmarks": "Content Benchmarks",
	"/content/human-qa": "QA Review",
	"/content/qa": "QA Checklist",
	"/content/qa-checks": "QA Content Checks",
	"/content/editor": "Content Editor",
	"/content/calendar": "Content Calendar",
	"/audit/technical": "Technical Site Audit",
	"/audit/tickets": "Tickets",
	"/support": "Get Support",
	"/support/tickets": "Support Tickets",
	"/planner": "Agile Plan",
	"/planner/offsite": "Offsite Links",
	"/planner/approvals": "Approvals",
	"/planner/catalog": "Task Catalog",
	"/planner/feedback": "Feedback",
	"/internal/preview": "Preview Features",
	"/comprehensive-site-audit": "Comprehensive Audit",
	"/seo/schema-management": "Schema Management",
	"/seo/serp-preview": "SERP Preview",
	"/seo/core-web-vitals": "Core Web Vitals",
	"/seo/competitor-compare": "Competitor Compare",
	"/seo/content-preferences": "Content Preferences",
	"/assets": "Image Assets",
	"/seo/keyword-research": "Keyword Research",
	"/seo/internal-pages": "Internal Pages",
	"/ads-tools": googleAdsLabel,
	"/ads-tools/keyword-research": "Keyword Research",
	"/ads-tools/campaigns": "Campaigns",
	"/ads-tools/account-audit": "Account Audit",
	"/ads-tools/client-dashboard": "Client Dashboard",
	"/ads-tools/management": "Management",
	"/ads-tools/campaign-settings": "Campaign Settings",
	"/ads-tools/weekly-management": "Client Dashboard",
	"/ads-tools/search-terms": "Search Terms",
	"/ads-tools/performance": "Performance",
	"/ads-tools/monthly-recaps": "Performance",
	"/clients": "Workspaces",
	"/clients/ads": "Ads Overview",
	"/clients/groups": "Groups",
	"/clients/summary": "Client Summary",
	"/ads-tools/whatconverts": "WhatConverts Leads",
	"/ads-tools/kpi-diagnostics": "Performance",
	"/ads-tools/budgets": "Budget Pacing",
	// CORE-K-01: the /ads-tools/cluster-refresh URL redirects to the
	// Keyword Research page's refresh subtab; the breadcrumb reads
	// "Keyword Research" the same as any other Keyword Research subtab.
	"/ads-tools/cluster-refresh": "Keyword Research",
	"/ads-tools/competitive": "Competitive Insights",
	// Without these two the top bar falls back to "Dashboard" (see
	// ModernMainLayout's `routeTitles[location.pathname] || "Dashboard"`),
	// which is what Art saw on both pages. Campaign QA is a section of
	// GoogleAdsOpsPage and Image Assets is its own route, but the lookup
	// is on pathname alone, so the nav url's ?platform= query is not part
	// of the key.
	"/ads-tools/campaign-qa": "Campaign QA",
	"/ads-tools/image-assets": "Image Assets",
	"/ads-tools/pmax": "Performance Max",
	"/ads-tools/placement-review": "Performance Max",
	"/ads-tools/ops": googleAdsLabel,
	"/google-ads/keyword-research": googleAdsLabel,
	"/google-ads/ops": googleAdsLabel,
	"/admin/google-ads": googleAdsLabel,
	"/admin/llm-benchmarks": "LLM Benchmarks",
	"/admin/research-coverage": "Research Coverage",
	"/credits": "Usage & credits",
	"/usage": "Usage & Cost",
	"/cost-attribution": "Cost Attribution",
};
